/**
 * @module utils/cookie-monster
 * @description When the user's session is idle, then periodically eat/delete cookies that aren't on the whitelist.
 */
import { getProp } from "utils/utils";
import includes from "lodash/includes";
import isNil from "lodash/isNil";
import { local } from "lib/store";
import logger from "utils/logger";
import { mark, measure, getEntriesByName } from "lib/performance/user-timing";
import tgamConfig from "config/tgam";
import { COOKIE_DOMAIN } from "lib/identity/api";

const debugTag = "[COOKIE-MONSTER]";
const cookieJarKey = "tgam.cookie-monster-last-ate"; // last run datetime

/**
 * @method hungryForCookies
 * @description Determine if cookies should be eaten, based on frequency and datetime of last meal.
 * @param {Number} now - current datetime value.
 * @returns {Boolean} true if hungry
 */
export function hungryForCookies(now) {
  const runtime = getProp(tgamConfig, "env.cookieMonsterHungry");
  if (!isNil(runtime) && runtime === "false") {
    logger.info(`${debugTag} is hungry: disabled`);
    return false; // no frequency check necessary
  }
  logger.groupCollapsed(`${debugTag} hungry`);

  const tag = "cookie_monster_hungry";
  mark(`${tag}_started`);

  // parseInt here because via environment-variables.jsp cookieMonsterFrequency will be a string
  const frequency = parseInt(tgamConfig.env.cookieMonsterFrequency, 10); // hours
  const cookieStorage = local.get(cookieJarKey); // epoch numeric value
  const previous = isNil(cookieStorage) ? new Date(0) : new Date(cookieStorage);
  const diff = Math.abs(now - previous) / 36e+5; // 3,600,000 - convert to hours
  const hungry = diff > frequency;

  // Epoch Timestamp Conversion Tools - https://www.epochconverter.com/
  logger.info(`${debugTag} ate last: ${previous}, epoch: ${previous.getTime()}`);
  logger.info(`${debugTag} eat now?: ${now}, epoch: ${now.getTime()}`);
  logger.info(`${debugTag} is hungry: ${hungry}, freq: ${frequency} hrs, diff: ${diff} hrs`);

  mark(`${tag}_done`);
  measure(tag); // from start to done

  // log how long it took for cookie monster to figure out if he's hungry
  const perfs = getEntriesByName(tag);
  if (perfs.length > 0) {
    logger.info(`${debugTag} thinking lasted for: ${perfs[0].duration} ms`);
  }

  logger.groupEnd(`${debugTag} hungry`);
  return hungry;
}

/**
 * @method eatTheCookies
 * @description Decide if/when and what cookies to eat.
 * @param {Number} now - current datetime value.
 */
export function eatTheCookies(now) {
  const tag = "cookie_monster_eat";
  mark(`${tag}_started`);

  const allCookies = mapCookieList(document.cookie);
  const grossCookies = mapWhiteList(tgamConfig.env.cookieMonsterWhitelist);
  const deliciousCookies = cookie => !includes(grossCookies, cookie);
  const myCookies = allCookies.filter(deliciousCookies);
  myCookies.forEach(name => removeCookie(name, COOKIE_DOMAIN, "/"));
  local.set(cookieJarKey, now.getTime());

  logger.info(`${debugTag} tried to eat: ${myCookies.join(", ")}`);

  mark(`${tag}_done`);
  measure(tag); // from start to done

  // log how long it took for cookie monster to eat his meal
  const perfs = getEntriesByName(tag);
  if (perfs.length > 0) {
    logger.info(`${debugTag} meal lasted for: ${perfs[0].duration} ms`);
  }
};

/**
 * @method removeCookie
 * @description When deleting a cookie, you must pass the exact same path
 * and domain attributes that were used to set the cookie
 * @param {String} name - of the cookie
 * @param {String} domain - of the cookie
 * @param {String} path - of the cookie
 */
function removeCookie(name, domain, path) {
  document.cookie = [
    `${name}=`, // blank value
    "expires=Thu, 01 Jan 1970 00:00:00 GMT", // epoch zero
    `domain=${domain}`,
    `path=${path}`
  ].join("; ");
}

/**
 * @method mapWhiteList
 * @description Map a cookie whitelist into a key list
 * @param {String} list - delimited by ,
 * @returns {Array} of cookie names
 */
function mapWhiteList(list = "") {
  return list.split(",");
}

/**
 * @method mapCookieList
 * @description Map a cookie list into a key list (disgard the associated values)
 * @param {String} list - delimited by ;
 * @returns {Array} of cookie names
 */
function mapCookieList(list = "") {
  return list.split(/;\s*/).map(i => i.split("=")[0]);
}
